import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useFormatters } from '../hooks/useFormatters';
import { useHeldWhileClosed } from '../hooks/useHeldWhileClosed';
import DialogErrorBanner from './common/DialogErrorBanner';
import {
  COST_GATE,
  STRATEGY_ACTIONS,
  adoptFragment,
  costGateMode,
  costGatePayloadDefect,
  costGateScope,
  hasInvalidOverride,
  initialRowStrategies,
  strategyFragment,
} from '../utils/writeGates';

function CurrencyChangeConfirmModal({
  open,
  changeContext: requestedContext,
  affectedItems: requestedItems,
  onConfirm,
  onCancel,
  loading = false,
  errorMessage = null,
}) {
  const { t } = useTranslation();
  const { formatNumber, formatCurrency, formatDate, parseNumber } = useFormatters();

  // Which of the two questions this payload is asking, and whether it is
  // answerable at all — both the rule in utils/writeGates.js, shared with the
  // write path, so the two cannot disagree about what a given `change_context`
  // means. `defect` non-null means the gate arrived short (no items, no
  // currency pair, no rate or no date and source for it): the dialog says so
  // and offers no Apply, because a
  // padded-out confirmation reads like a complete question and can be answered
  // like one.
  //
  // The gate on screen is the requested one while open, and the last one
  // shown while it fades out (useHeldWhileClosed). Never opened there is
  // none, and this dialog stays mounted with a null change_context between
  // gates — so nothing below reads the payload unless a gate is on screen
  // and cleared (`ready`), and every reader past this point relies on what
  // `costGatePayloadDefect` checked.
  const shown = useHeldWhileClosed(open, {
    changeContext: requestedContext, affectedItems: requestedItems,
  });
  const { changeContext, affectedItems } = shown ?? {};
  const defect = shown ? costGatePayloadDefect(changeContext, affectedItems) : null;
  const ready = shown != null && !defect;
  const mode = ready ? costGateMode(changeContext) : null;
  // Model B: electing a new default supplier ADOPTS that supplier's price as
  // the item's cost of record (no keep/convert reinterpretation). Render a
  // plain from → to confirmation. An unpriced supplier is never gated, so every
  // row here carries a new cost. Distinct from the supplier-currency-edit
  // trigger below, which keeps the keep/convert/override machinery.
  const isAdopt = mode === COST_GATE.ADOPT;

  // How many items the gate covers versus how many rows it sent — the rule in
  // utils/writeGates.js.
  const { count: affectedCount, shown: shownCount, truncated: isTruncated } = ready
    ? costGateScope(changeContext, affectedItems)
    : { count: 0, shown: 0, truncated: false };

  const isSingleItem = affectedCount === 1;
  // When the merchant's batch save spans multiple distinct currency pairs
  // (e.g. EUR→CZK on row 1, USD→GBP on row 2), the top-level change_context
  // can no longer truthfully describe every row — the backend sets
  // `multi_currency: true` and each affected_item carries its own FX info.
  // Multi-pair mode hides the single-rate header and renders per-row
  // currency labels + per-row rate column inside the table.
  const isMultiCurrency = ready && changeContext.multi_currency === true;

  const [defaultAction, setDefaultAction] = useState('keep');
  const [reviewEach, setReviewEach] = useState(false);
  const [rowStrategies, setRowStrategies] = useState({});

  // Reset the per-row strategy machine each time the dialog transitions to
  // open. Done during render — React's endorsed alternative to a
  // setState-in-effect — so the initialised state is present on the first
  // painted frame (no flash of stale rows, no cascading second render).
  // useResolutionGate's `request()` sets affectedItems and open in one batch,
  // so affectedItems already holds the current batch on the opening render.
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      // Single-item and multi-currency batches force per-row review: the
      // global "keep all in <newCcy>" / "convert all" radio can't truthfully
      // describe a single row or span mixed currency pairs. A single-currency
      // multi-item batch keeps the radio default.
      //
      // …unless the rows are a sample. Reviewing item by item is an offer to
      // decide EVERY item, and a sample cannot honour it — the answer for the
      // rows nobody was shown would be whatever the hidden blanket action
      // happened to be. At that size the question is a blanket one.
      setReviewEach((isSingleItem || isMultiCurrency) && !isTruncated);
      setDefaultAction('keep');
      // A defective gate has no rows to decide.
      setRowStrategies(ready ? initialRowStrategies(affectedItems) : {});
    }
  }

  // The merchant reads the banner; a developer needs the shape that broke.
  useEffect(() => {
    if (defect) console.error('Cost-change confirmation payload unusable:', defect);
  }, [defect]);

  const updateRowAction = (itemId, action) => {
    setRowStrategies(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), action },
    }));
  };

  const updateRowValue = (itemId, new_value) => {
    setRowStrategies(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { action: 'override' }), new_value },
    }));
  };

  const submitDisabled = loading
    || !open
    || !ready
    || (reviewEach && hasInvalidOverride(affectedItems, rowStrategies, parseNumber));

  // The modal owns the adopt-vs-reinterpret distinction, so it emits the
  // request-body FRAGMENT the caller spreads straight into its retry — every
  // caller stays trigger-agnostic. Adopt confirms with a bare
  // `confirm_cost_change` flag (no per-item choice); reinterpret carries the
  // per-item keep/convert/override `currency_change_strategy` object.
  const handleConfirm = () => {
    if (isAdopt) {
      onConfirm(adoptFragment());
      return;
    }
    // A single-item batch has no blanket choice to make, so it always sends the
    // one row as an override — `reviewEach` is forced true for it on open.
    onConfirm(strategyFragment({
      affectedItems, defaultAction, reviewEach, rowStrategies, parseNumber,
    }));
  };

  // The reinterpret question. Called only for an open, cleared reinterpret
  // gate, so the pair, the rate and its date are all there to read
  // — `costGatePayloadDefect` checked them, and each row's own in multi-pair
  // mode.
  const renderReinterpret = () => {
    const oldCcy = changeContext.old_currency;
    const newCcy = changeContext.new_currency;
    const contextMessage = isMultiCurrency
      ? t('currencyChange.contextMessageMulti', { count: affectedCount })
      : t('currencyChange.contextMessage', { oldCcy, newCcy });

    // Said whenever the rows are a sample, in the same block as the question: a
    // list the merchant reads as complete is one they approve as complete.
    const truncationNotice = isTruncated
      // `total`, not `count`: i18next reads `count` as the plural selector, and
      // both numbers here are locale-formatted strings.
      ? t('currencyChange.truncatedNotice', {
        shown: formatNumber(shownCount, 0),
        total: formatNumber(affectedCount, 0),
      })
      : null;

    // Single-pair: one rate line in the header. Multi-pair: per-row rate
    // cells inside the table render the FX details — header rate line is
    // omitted because it would describe only the first pair.
    const rateMessage = isMultiCurrency ? null : t('currencyChange.rateMessage', {
      oldCcy,
      newCcy,
      rate: formatNumber(changeContext.fx_rate, 4),
      rateDate: formatDate(changeContext.fx_rate_date),
    });

    // The supplier's locked rate belongs to the old currency pair and goes
    // with it (backend `clears_fx_lock`): said before the merchant confirms.
    const lockNotice = changeContext.clears_fx_lock === true
      ? t('currencyChange.clearsLockNotice')
      : null;

    return (
      <>
        <Alert severity="info" variant="outlined">
          <Stack spacing={1}>
            <Typography variant="body2">{contextMessage}</Typography>
            {rateMessage && <Typography variant="body2">{rateMessage}</Typography>}
            {truncationNotice && <Typography variant="body2">{truncationNotice}</Typography>}
            {lockNotice && <Typography variant="body2">{lockNotice}</Typography>}
          </Stack>
        </Alert>

        {!isSingleItem && (
          <FormControl>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('currencyChange.defaultActionLabel')}
            </Typography>
            <RadioGroup
              value={reviewEach ? 'review' : defaultAction}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'review') {
                  setReviewEach(true);
                } else {
                  setReviewEach(false);
                  setDefaultAction(v);
                }
              }}
            >
              <FormControlLabel
                value="keep"
                control={<Radio />}
                label={isMultiCurrency
                  ? t('currencyChange.keepAllMulti')
                  : t('currencyChange.keepAll', { newCcy })}
              />
              <FormControlLabel
                value="convert"
                control={<Radio />}
                label={isMultiCurrency
                  ? t('currencyChange.convertAllMulti')
                  : t('currencyChange.convertAll')}
              />
              {/* Not offered over a sample — see the reviewEach reset above. */}
              {!isTruncated && (
                <FormControlLabel
                  value="review"
                  control={<Radio />}
                  label={t('currencyChange.reviewEach')}
                />
              )}
            </RadioGroup>
          </FormControl>
        )}

        {reviewEach && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('currencyChange.col.item')}</TableCell>
                  <TableCell align="right">
                    {isMultiCurrency
                      ? t('currencyChange.col.currentCostGeneric')
                      : t('currencyChange.col.currentCost', { oldCcy })}
                  </TableCell>
                  <TableCell align="right">
                    {isMultiCurrency
                      ? t('currencyChange.col.convertedCostGeneric')
                      : t('currencyChange.col.convertedCost', { newCcy })}
                  </TableCell>
                  {isMultiCurrency && (
                    <TableCell>{t('currencyChange.col.rate')}</TableCell>
                  )}
                  <TableCell>{t('currencyChange.col.action')}</TableCell>
                  <TableCell align="right">
                    {isMultiCurrency
                      ? t('currencyChange.col.overrideValueGeneric')
                      : t('currencyChange.col.overrideValue', { newCcy })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {affectedItems.map(row => {
                  const strat = rowStrategies[row.item_id] || { action: 'keep', new_value: '' };
                  // Over several pairs each row names its own pair and rate;
                  // over one, the header's pair is every row's.
                  const rowOldCcy = isMultiCurrency ? row.old_currency : oldCcy;
                  const rowNewCcy = isMultiCurrency ? row.new_currency : newCcy;
                  return (
                    <TableRow key={row.item_id}>
                      {/* One server-built label (product name + SKU) — an
                          item code column here printed a variant-id suffix on
                          Shopify stores. */}
                      <TableCell>{row.item_label}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.current_cost, rowOldCcy)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.converted_cost, rowNewCcy)}
                      </TableCell>
                      {isMultiCurrency && (
                        <TableCell>
                          <Typography variant="body2">
                            {t('currencyChange.rateInline', {
                              oldCcy: rowOldCcy,
                              newCcy: rowNewCcy,
                              rate: formatNumber(row.fx_rate, 4),
                            })}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(row.fx_rate_date)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Select
                          size="small"
                          value={strat.action}
                          onChange={(e) => updateRowAction(row.item_id, e.target.value)}
                          disabled={loading}
                        >
                          {STRATEGY_ACTIONS.map(a => (
                            <MenuItem key={a} value={a}>
                              {t(`currencyChange.action.${a}`)}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          value={strat.new_value || ''}
                          onChange={(e) => updateRowValue(row.item_id, e.target.value)}
                          disabled={strat.action !== 'override' || loading}
                          slotProps={{ htmlInput: { inputMode: 'decimal', style: { textAlign: 'right' } } }}
                          placeholder={strat.action === 'override' ? `${formatNumber(0, 4)} ${rowNewCcy}` : ''}
                          sx={{ width: 140 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </>
    );
  };

  // The reinterpret question can end in a per-item table with a rate and an
  // input on every row, so it takes the table width; the adopt list is three
  // columns and keeps the form width.
  const sizeClass = mode === COST_GATE.REINTERPRET ? 'dialog-form--wide' : '';

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      className={`dialog-form ${sizeClass}`}
    >
      <DialogTitle>
        {defect
          ? t('currencyChange.payloadInvalidTitle')
          : isAdopt ? t('currencyChange.adopt.title') : t('currencyChange.title')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {defect ? (
            // The gate arrived unrenderable. Say so and show nothing else: a
            // dialog that lists no items, or names no currency pair, is a
            // confirmation the merchant could approve without the facts.
            <Alert severity="error" variant="outlined">
              <Typography variant="body2">{t('currencyChange.payloadInvalid')}</Typography>
            </Alert>
          ) : isAdopt ? (
            <>
              <Alert severity="info" variant="outlined">
                <Typography variant="body2">
                  {isSingleItem
                    ? t('currencyChange.adopt.message')
                    : t('currencyChange.adopt.messageMulti', { count: affectedCount })}
                </Typography>
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('currencyChange.col.item')}</TableCell>
                      <TableCell align="right">{t('currencyChange.adopt.col.fromCost')}</TableCell>
                      <TableCell align="right">{t('currencyChange.adopt.col.toCost')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {affectedItems.map(row => (
                      <TableRow key={row.item_id}>
                        {/* One server-built label (product name + SKU) — an
                            item code column here printed a variant-id suffix on
                            Shopify stores. */}
                        <TableCell>{row.item_label}</TableCell>
                        <TableCell align="right">
                          {row.from_cost != null
                            ? formatCurrency(row.from_cost, row.from_currency)
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(row.to_cost, row.to_currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : mode === COST_GATE.REINTERPRET ? renderReinterpret() : null}
        </Stack>

        <DialogErrorBanner error={errorMessage} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitDisabled}
          loading={loading}
        >
          {t('currencyChange.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CurrencyChangeConfirmModal;
